import React, {useState, useCallback, memo, useEffect} from 'react';
import {
    Button, Box, Typography, Alert, CircularProgress,
    TextField, Switch, FormControlLabel, Grid, MenuItem, Card, CardHeader, CardContent, CardActions, ListSubheader, Autocomplete,
    FormControl, InputLabel, Select
} from "@mui/material";
import {useDispatch} from "react-redux";
import {fetchSettings, updateSetting} from "../../Redux/Action";
import {
    getCompatibleUITypes,
    getSuggestedUIType,
    getSuggestedOptionType,
    validateFormCompatibility
} from "./FieldRestrictions";
import {DialogCard, Loader} from "../../../../../Components";
import {setModal} from "../../../../Common/App";

export const SettingsEditModal = memo(({setting, onClose, onSave}) => {
    const dispatch = useDispatch();
    
    const [state, setState] = useState({
        formValues: {},
        isLoading: false,
        isInitialized: false,
        hasUserInteracted: false,
        validationErrors: {},
        serverErrors: {},
        message: "",
        status: "info",
        allSettings: [],
        restrictionErrors: {},
        restrictionWarnings: {},
        compatibleUITypes: [],
        existingCategories: [],
        existingSubCategories: [],
        localOptions: [],
        editingOptionIndex: null,
        newOption: {label: '', value: ''},
        apiEndpoints: [],
        apiPreview: [],
        loadingApiPreview: false
    });

    const initializeFormValues = useCallback((setting) => {
        if (!setting) return;
        
        const parameterType = setting.parameter_type || 'STRING';
        const compatibleTypes = getCompatibleUITypes(parameterType);
        
        let options = setting.options || [];
        if (typeof options === 'string') {
            try {
                options = JSON.parse(options);
            } catch (e) {
                options = [];
            }
        }
        
        setState(prev => ({
            ...prev,
            compatibleUITypes: compatibleTypes,
            formValues: {
                parameter_name: setting.parameter_name || '',
                parameter_type: parameterType,
                parameter_value: setting.parameter_value || '',
                default_value: setting.default_value || '',
                title: setting.title || '',
                description: setting.description || '',
                tooltip: setting.tooltip || '',
                category: setting.category || '',
                sub_category: setting.sub_category || '',
                type: setting.type || 'text',
                ui_group: setting.ui_group || '',
                sequence: setting.sequence || 0,
                option_type: setting.option_type || 'STATIC',
                option_config: setting.option_config || {},
                is_active: setting.is_active ?? true,
                is_editable: setting.is_editable ?? true,
                validation_rules: typeof setting.validation_rules === 'string' ? setting.validation_rules : JSON.stringify(setting.validation_rules || {}),
                validation_message: setting.validation_message || '',
                parent_id: setting.parent_id || ''
            },
            localOptions: Array.isArray(options) ? options : [],
            isInitialized: true
        }));
    }, []);

    const fetchAllSettings = useCallback(async () => {
        await fetchSettings({dispatch, filters: {paginate: 'false', sort_by: 'category', sort_dir: 'asc'}}).then((res) => {
            if(res['success'] && res['data']){
                setState(prev => ({
                    ...prev,
                    allSettings: res.data,
                    existingCategories: [...new Set(res.data.map(s => s.category).filter(Boolean))].sort(),
                    existingSubCategories: [...new Set(res.data.map(s => s.sub_category).filter(Boolean))].sort(),
                    apiEndpoints: [
                        {value: '/api/users', label: 'Users List'},
                        {value: '/api/roles', label: 'Roles List'},
                        {value: '/api/departments', label: 'Departments List'},
                        {value: '/api/statuses', label: 'Statuses List'}
                    ]
                }));
            }
        });
    }, [dispatch]);

    const groupedSettings = useCallback(() => {
        const filteredSettings = state.allSettings.filter(s => s.id !== setting?.id);
        const grouped = {};
        
        filteredSettings.forEach(setting => {
            const category = setting.category || 'Other';
            if (!grouped[category]) {
                grouped[category] = [];
            }
            grouped[category].push(setting);
        });
        
        const sortedCategories = Object.keys(grouped).sort();
        const sortedGrouped = {};
        sortedCategories.forEach(category => {
            sortedGrouped[category] = grouped[category].sort((a, b) => 
                (a.title || a.parameter_name).localeCompare(b.title || b.parameter_name)
            );
        });
        
        return sortedGrouped;
    }, [state.allSettings, setting?.id]);

    useEffect(() => {
        if (setting) {
            initializeFormValues(setting);
        }
        fetchAllSettings();
    }, [setting, initializeFormValues, fetchAllSettings]);

    const validateForm = useCallback((formData, localOptionsData) => {
        const errors = {};
        
        if (['select', 'radio', 'checkbox'].includes(formData.type)) {
            if (!formData.option_type) {
                errors.option_type = 'Option type is required for select/radio/checkbox fields';
            } else if (formData.option_type === 'STATIC') {
                if (!localOptionsData || !Array.isArray(localOptionsData) || localOptionsData.length === 0) {
                    errors.options = 'Static options are required for select/radio/checkbox fields';
                }
            } else if (formData.option_type === 'API') {
                if (!formData.option_config?.endpoint) {
                    errors.option_config = 'API endpoint is required for API option type';
                }
            }
        }
        
        if (formData.validation_rules) {
            try {
                const rules = typeof formData.validation_rules === 'string' 
                    ? JSON.parse(formData.validation_rules) 
                    : formData.validation_rules;
                
                const compatibleRules = getCompatibleValidationRules(formData.parameter_type);
                const invalidRules = Object.keys(rules).filter(rule => !compatibleRules.includes(rule));
                
                if (invalidRules.length > 0) {
                    errors.validation_rules = `${formData.parameter_type} type doesn't support these validation rules: ${invalidRules.join(', ')}`;
                }
            } catch (e) {
                errors.validation_rules = 'Invalid validation rules format';
            }
        }
        
        return errors;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getCompatibleValidationRules is stable
    }, []);

    const getCompatibleValidationRules = useCallback((parameterType) => {
        const rules = {
            STRING: ['required', 'minLength', 'maxLength', 'pattern', 'email', 'url'],
            INTEGER: ['required', 'min', 'max'],
            FLOAT: ['required', 'min', 'max'],
            BOOLEAN: ['required'],
            JSON: ['required']
        };
        return rules[parameterType] || [];
    }, []);

    const clearFieldError = useCallback((field) => {
        setState(prev => {
            const newValidationErrors = {...prev.validationErrors};
            const newServerErrors = {...prev.serverErrors};
            delete newValidationErrors[field];
            delete newServerErrors[field];
            
            return {
                ...prev,
                validationErrors: newValidationErrors,
                serverErrors: newServerErrors
            };
        });
    }, []);

    const handleFieldChange = useCallback((field, value) => {
        clearFieldError(field);
        
        setState(prev => {
            const newFormValues = {...prev.formValues, [field]: value};
            
            if (field === 'parameter_type') {
                const compatibleTypes = getCompatibleUITypes(value);
                const suggestedType = getSuggestedUIType(value, prev.formValues.type);
                newFormValues.type = suggestedType;
                
                if (value === 'BOOLEAN') {
                    newFormValues.parameter_value = 'false';
                    newFormValues.validation_rules = '';
                } else if (value === 'INTEGER' || value === 'FLOAT') {
                    newFormValues.parameter_value = '0';
                } else if (value === 'STRING') {
                    newFormValues.parameter_value = '';
                } else if (value === 'JSON') {
                    newFormValues.parameter_value = '{}';
                }
                
                return {
                    ...prev,
                    formValues: newFormValues,
                    compatibleUITypes: compatibleTypes,
                    hasUserInteracted: true
                };
            }
            
            if (field === 'type') {
                const suggestedOptionType = getSuggestedOptionType(value);
                if (suggestedOptionType && prev.formValues.option_type !== suggestedOptionType) {
                    newFormValues.option_type = suggestedOptionType;
                }
            }
            
            const validationData = {...newFormValues, options: prev.localOptions};
            const validation = validateFormCompatibility(validationData);
            
            return {
                ...prev,
                formValues: newFormValues,
                restrictionErrors: validation.errors,
                restrictionWarnings: validation.warnings,
                hasUserInteracted: true
            };
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clearFieldError is stable
    }, []);

    useEffect(() => {
        if (state.isInitialized) {
            const validationData = {...state.formValues, options: state.localOptions};
            const validation = validateFormCompatibility(validationData);
            setState(prev => ({
                ...prev,
                restrictionErrors: validation.errors,
                restrictionWarnings: validation.warnings
            }));
        }
    }, [state.localOptions, state.formValues, state.isInitialized]);

    const handleAddOption = useCallback(() => {
        if (state.newOption.label && state.newOption.value) {
            const option = {
                id: `option_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                label: state.newOption.label,
                value: state.newOption.value
            };
            setState(prev => ({
                ...prev,
                localOptions: [...prev.localOptions, option],
                newOption: {label: '', value: ''}
            }));
        }
    }, [state.newOption]);

    const handleEditOption = useCallback((index) => {
        setState(prev => ({
            ...prev,
            editingOptionIndex: index,
            newOption: prev.localOptions[index]
        }));
    }, []);

    const handleUpdateOption = useCallback(() => {
        if (state.editingOptionIndex !== null && state.newOption.label && state.newOption.value) {
            setState(prev => ({
                ...prev,
                localOptions: prev.localOptions.map((option, index) => 
                    index === prev.editingOptionIndex ? {...option, ...prev.newOption} : option
                ),
                editingOptionIndex: null,
                newOption: {label: '', value: ''}
            }));
        }
    }, [state.editingOptionIndex, state.newOption]);

    const handleDeleteOption = useCallback((index) => {
        setState(prev => ({
            ...prev,
            localOptions: prev.localOptions.filter((_, i) => i !== index)
        }));
    }, []);

    const handleCancelEdit = useCallback(() => {
        setState(prev => ({
            ...prev,
            editingOptionIndex: null,
            newOption: {label: '', value: ''}
        }));
    }, []);

    const handleApiPreview = useCallback(async () => {
        if (!state.formValues.option_config?.endpoint) return;
        
        setState(prev => ({...prev, loadingApiPreview: true}));
        
        await new Promise(resolve => setTimeout(resolve, 1000)).then(() => {
            setState(prev => ({
                ...prev,
                apiPreview: [
                    {id: '1', label: 'Admin', value: 'admin'},
                    {id: '2', label: 'User', value: 'user'},
                    {id: '3', label: 'Manager', value: 'manager'}
                ],
                loadingApiPreview: false
            }));
        });
    }, [state.formValues.option_config]);

    const handleSave = useCallback(async () => {
        setState(prev => ({...prev, isLoading: true, message: "", status: "info"}));
        
        const clientErrors = validateForm(state.formValues, state.localOptions);
        
        if (Object.keys(clientErrors).length > 0) {
            setState(prev => ({
                ...prev,
                validationErrors: clientErrors,
                status: "error",
                message: "Please fix the validation errors before saving.",
                isLoading: false
            }));
            return;
        }
        
        const payload = {
            parameter_name: state.formValues.parameter_name,
            parameter_type: state.formValues.parameter_type,
            parameter_value: state.formValues.parameter_value,
            default_value: state.formValues.default_value,
            title: state.formValues.title,
            description: state.formValues.description,
            tooltip: state.formValues.tooltip,
            category: state.formValues.category,
            sub_category: state.formValues.sub_category,
            type: state.formValues.type,
            ui_group: state.formValues.ui_group,
            sequence: state.formValues.sequence,
            option_type: state.formValues.option_type,
            option_config: state.formValues.option_config,
            options: state.formValues.option_type === 'STATIC' ? state.localOptions : null,
            is_active: state.formValues.is_active,
            is_editable: state.formValues.is_editable,
            validation_rules: state.formValues.validation_rules,
            validation_message: state.formValues.validation_message,
            parent_id: state.formValues.parent_id
        };

        await updateSetting({dispatch, data: {id: setting.id, ...payload}}).then((res) => {
            if(res['success']){
                setState(prev => ({
                    ...prev,
                    message: "Setting updated successfully",
                    status: "success"
                }));
                dispatch(setModal({component: null, open: false}));
            } else {
                setState(prev => ({
                    ...prev,
                    message: res?.message || "Failed to update setting",
                    status: "error"
                }));
            }
        })
    }, [state.formValues, state.localOptions, validateForm, setting, dispatch]);

    const handleClose = useCallback(() => {
        dispatch(setModal({component: null, open: false}));
        if (onClose) onClose();
    }, [dispatch, onClose]);

    if (!state.isInitialized) {
        return (
            <DialogCard>
                <CardContent>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                </CardContent>
            </DialogCard>
        );
    }

    return (
        <DialogCard>
            <CardHeader 
                title={`Edit Setting: ${setting.title}`}
                action={
                    <Box display="flex" alignItems="center" gap={1}>
                        <Loader isLoading={state.isLoading} type="linear" />
                    </Box>
                }
            />
            
            <CardContent>
                {state.message && (
                    <Alert severity={state.status} sx={{mb: 2}}>
                        {state.message}
                    </Alert>
                )}

                {(Object.keys(state.validationErrors).filter(key => state.validationErrors[key]).length > 0 || Object.keys(state.serverErrors).filter(key => state.serverErrors[key]).length > 0) && (
                    <Alert severity="error" sx={{mb: 2}}>
                        <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1}}>
                            Validation Errors:
                        </Typography>
                        {Object.keys(state.validationErrors).map(field => (
                            state.validationErrors[field] && (
                                <Typography key={field} variant="body2">
                                    • {field}: {state.validationErrors[field]}
                                </Typography>
                            )
                        ))}
                        {Object.keys(state.serverErrors).map(field => (
                            state.serverErrors[field] && (
                                <Typography key={field} variant="body2">
                                    • {field}: {state.serverErrors[field]}
                                </Typography>
                            )
                        ))}
                    </Alert>
                )}

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{mb: 2, fontWeight: 600}}>
                            Basic Configuration
                        </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Parameter Name"
                            value={state.formValues.parameter_name}
                            onChange={(e) => handleFieldChange('parameter_name', e.target.value)}
                            variant="outlined"
                            size="small"
                            required
                            placeholder="e.g., enable-sso"
                        />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth variant="outlined" size="small" required>
                            <InputLabel htmlFor="parameter-type-select">Parameter Type</InputLabel>
                            <Select
                                value={state.formValues.parameter_type || ''}
                                onChange={(e) => handleFieldChange('parameter_type', e.target.value)}
                                label="Parameter Type"
                                inputProps={{
                                    name: 'parameter_type',
                                    id: 'parameter-type-select',
                                }}
                                variant="outlined"
                            >
                                <MenuItem value="STRING">String</MenuItem>
                                <MenuItem value="BOOLEAN">Boolean</MenuItem>
                                <MenuItem value="INTEGER">Integer</MenuItem>
                                <MenuItem value="FLOAT">Float</MenuItem>
                                <MenuItem value="JSON">JSON</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{mb: 2, fontWeight: 600, mt: 2}}>
                            Display Configuration
                        </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Title"
                            value={state.formValues.title}
                            onChange={(e) => handleFieldChange('title', e.target.value)}
                            variant="outlined"
                            size="small"
                            placeholder="e.g., Enable SSO"
                        />
                    </Grid>
                    
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            value={state.formValues.description}
                            onChange={(e) => handleFieldChange('description', e.target.value)}
                            variant="outlined"
                            size="small"
                            multiline
                            rows={2}
                            placeholder="e.g., Enable single sign-on authentication"
                        />
                    </Grid>
                    
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Tooltip"
                            value={state.formValues.tooltip}
                            onChange={(e) => handleFieldChange('tooltip', e.target.value)}
                            variant="outlined"
                            size="small"
                            placeholder="e.g., Turn off when SSO is enabled"
                        />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            freeSolo
                            options={state.existingCategories}
                            value={state.formValues.category}
                            onChange={(event, newValue) => handleFieldChange('category', newValue || '')}
                            onInputChange={(event, newInputValue) => handleFieldChange('category', newInputValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Category"
                                    variant="outlined"
                                    size="small"
                                    placeholder="e.g., login-settings"
                                />
                            )}
                        />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            freeSolo
                            options={state.existingSubCategories}
                            value={state.formValues.sub_category}
                            onChange={(event, newValue) => handleFieldChange('sub_category', newValue || '')}
                            onInputChange={(event, newInputValue) => handleFieldChange('sub_category', newInputValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Sub Category"
                                    variant="outlined"
                                    size="small"
                                    placeholder="e.g., sso"
                                />
                            )}
                        />
                    </Grid>

                    {state.restrictionErrors && Object.keys(state.restrictionErrors).length > 0 && (
                        <Grid item xs={12}>
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                <Typography variant="subtitle2" component="strong" sx={{ display: 'block', mb: 0.5 }}>
                                    Field Compatibility Issues
                                </Typography>
                                {Object.entries(state.restrictionErrors).filter(([, v]) => v).map(([key, msg]) => (
                                    <Typography key={key} variant="body2" component="span" sx={{ display: 'block' }}>
                                        {msg}
                                    </Typography>
                                ))}
                            </Alert>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{mb: 2, fontWeight: 600, mt: 2}}>
                            UI Configuration
                        </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth variant="outlined" size="small">
                            <InputLabel htmlFor="ui-type-select">UI Type</InputLabel>
                            <Select
                                value={state.formValues.type || ''}
                                onChange={(e) => handleFieldChange('type', e.target.value)}
                                label="UI Type"
                                inputProps={{
                                    name: 'type',
                                    id: 'ui-type-select',
                                }}
                                variant="outlined"
                            >
                                {state.compatibleUITypes.map(type => (
                                    <MenuItem key={type} value={type}>
                                        {type === 'text' ? 'Text Input' :
                                         type === 'textarea' ? 'Text Area' :
                                         type === 'select' ? 'Dropdown' :
                                         type === 'radio' ? 'Radio Buttons' :
                                         type === 'checkbox' ? 'Checkboxes' :
                                         type === 'switch' ? 'Toggle Switch' :
                                         type === 'number' ? 'Number Input' :
                                         type}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="UI Group"
                            value={state.formValues.ui_group}
                            onChange={(e) => handleFieldChange('ui_group', e.target.value)}
                            variant="outlined"
                            size="small"
                            placeholder="e.g., authentication"
                        />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Sequence"
                            type="number"
                            value={state.formValues.sequence}
                            onChange={(e) => handleFieldChange('sequence', parseInt(e.target.value) || 0)}
                            variant="outlined"
                            size="small"
                            placeholder="e.g., 1"
                        />
                    </Grid>
                    
                    {(state.formValues.type === 'select' || state.formValues.type === 'radio' || state.formValues.type === 'checkbox') && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{mb: 2, fontWeight: 600, mt: 2}}>
                                    Options Configuration
                                </Typography>
                            </Grid>
                            
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Option Type"
                                    value={state.formValues.option_type}
                                    onChange={(e) => handleFieldChange('option_type', e.target.value)}
                                    variant="outlined"
                                    size="small"
                                >
                                    <MenuItem value="STATIC">Static Options</MenuItem>
                                    <MenuItem value="API">API Endpoint</MenuItem>
                                </TextField>
                            </Grid>
                            
                            {state.formValues.option_type === 'STATIC' && (
                                <>
                                    <Grid item xs={12}>
                                        <Card variant="outlined" sx={{p: 2}}>
                                            <Typography variant="subtitle2" sx={{mb: 2}}>
                                                Add New Option
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Label"
                                                        value={state.newOption.label}
                                                        onChange={(e) => setState(prev => ({...prev, newOption: {...prev.newOption, label: e.target.value}}))}
                                                        variant="outlined"
                                                        size="small"
                                                        placeholder="e.g., Admin"
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Value"
                                                        value={state.newOption.value}
                                                        onChange={(e) => setState(prev => ({...prev, newOption: {...prev.newOption, value: e.target.value}}))}
                                                        variant="outlined"
                                                        size="small"
                                                        placeholder="e.g., admin"
                                                        required
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Box sx={{display: 'flex', gap: 1}}>
                                                        {state.editingOptionIndex !== null ? (
                                                            <>
                                                                <Button
                                                                    variant="contained"
                                                                    size="small"
                                                                    onClick={handleUpdateOption}
                                                                    disabled={!state.newOption.label || !state.newOption.value}
                                                                >
                                                                    Update
                                                                </Button>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    onClick={handleCancelEdit}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                            </>
                                                        ) : (
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                onClick={handleAddOption}
                                                                disabled={!state.newOption.label || !state.newOption.value}
                                                            >
                                                                Add Option
                                                            </Button>
                                                        )}
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Card>
                                    </Grid>
                                    
                                    {Array.isArray(state.localOptions) && state.localOptions.length > 0 && (
                                        <Grid item xs={12}>
                                            <Card variant="outlined" sx={{p: 2}}>
                                                <Typography variant="subtitle2" sx={{mb: 2}}>
                                                    Current Options ({state.localOptions.length})
                                                </Typography>
                                                {state.localOptions.map((option, index) => (
                                                    <Box key={option.id} sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                                        <TextField
                                                            size="small"
                                                            value={option.label}
                                                            disabled
                                                            variant="outlined"
                                                            sx={{flex: 1}}
                                                        />
                                                        <TextField
                                                            size="small"
                                                            value={option.value}
                                                            disabled
                                                            variant="outlined"
                                                            sx={{flex: 1}}
                                                        />
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => handleEditOption(index)}
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="error"
                                                            onClick={() => handleDeleteOption(index)}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </Box>
                                                ))}
                                            </Card>
                                        </Grid>
                                    )}
                                </>
                            )}
                            
                            {state.formValues.option_type === 'API' && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            select
                                            label="API Endpoint"
                                            value={
                                                state.apiEndpoints.some(e => e.value === state.formValues.option_config?.endpoint)
                                                    ? state.formValues.option_config.endpoint
                                                    : ''
                                            }
                                            onChange={(e) => handleFieldChange('option_config', { 
                                                ...state.formValues.option_config, 
                                                endpoint: e.target.value 
                                            })}
                                            variant="outlined"
                                            size="small"
                                        >
                                            {state.apiEndpoints.map(endpoint => (
                                                <MenuItem key={endpoint.value} value={endpoint.value}>
                                                    {endpoint.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            onClick={handleApiPreview}
                                            disabled={!state.formValues.option_config?.endpoint || state.loadingApiPreview}
                                            startIcon={state.loadingApiPreview ? <CircularProgress size={16} /> : null}
                                        >
                                            {state.loadingApiPreview ? 'Loading...' : 'Preview Data'}
                                        </Button>
                                    </Grid>
                                    
                                    {state.apiPreview.length > 0 && (
                                        <Grid item xs={12}>
                                            <Card variant="outlined" sx={{p: 2}}>
                                                <Typography variant="subtitle2" sx={{mb: 2}}>
                                                    API Preview ({state.apiPreview.length} items)
                                                </Typography>
                                                {state.apiPreview.map((item, index) => (
                                                    <Box key={item.id} sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                                        <TextField
                                                            size="small"
                                                            value={item.label}
                                                            disabled
                                                            variant="outlined"
                                                            sx={{flex: 1}}
                                                        />
                                                        <TextField
                                                            size="small"
                                                            value={item.value}
                                                            disabled
                                                            variant="outlined"
                                                            sx={{flex: 1}}
                                                        />
                                                    </Box>
                                                ))}
                                            </Card>
                                        </Grid>
                                    )}
                                </>
                            )}
                        </>
                    )}
                    
                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{mb: 2, fontWeight: 600, mt: 2}}>
                            Value Configuration
                        </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Parameter Value"
                            value={state.formValues.parameter_value}
                            onChange={(e) => handleFieldChange('parameter_value', e.target.value)}
                            variant="outlined"
                            size="small"
                            placeholder="e.g., true"
                        />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Default Value"
                            value={state.formValues.default_value}
                            onChange={(e) => handleFieldChange('default_value', e.target.value)}
                            variant="outlined"
                            size="small"
                            placeholder="e.g., false"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{mb: 2, fontWeight: 600, mt: 2}}>
                            Control Settings
                        </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={state.formValues.is_active}
                                    onChange={(e) => handleFieldChange('is_active', e.target.checked)}
                                />
                            }
                            label="Active"
                        />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={state.formValues.is_editable}
                                    onChange={(e) => handleFieldChange('is_editable', e.target.checked)}
                                />
                            }
                            label="Editable"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{mb: 2, fontWeight: 600, mt: 2}}>
                            Validation Configuration
                        </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Validation Rules"
                            value={state.formValues.validation_rules}
                            onChange={(e) => handleFieldChange('validation_rules', e.target.value)}
                            variant="outlined"
                            size="small"
                            multiline
                            rows={2}
                            placeholder='e.g., {"min": 1, "max": 100}'
                            error={Boolean(state.restrictionErrors?.validation_rules)}
                            helperText={state.restrictionErrors?.validation_rules}
                        />
                    </Grid>
                    
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Validation Message"
                            value={state.formValues.validation_message}
                            onChange={(e) => handleFieldChange('validation_message', e.target.value)}
                            variant="outlined"
                            size="small"
                            placeholder="e.g., Value must be between 1 and 100"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{mb: 2, fontWeight: 600, mt: 2}}>
                            Dependency
                        </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            select
                            label="Parent Field"
                            value={state.formValues.parent_id}
                            onChange={(e) => handleFieldChange('parent_id', e.target.value)}
                            variant="outlined"
                            size="small"
                        >
                            <MenuItem value="">No Parent Field</MenuItem>
                            {Object.entries(groupedSettings()).map(([category, settings]) => [
                                <ListSubheader key={`header-${category}`} sx={{fontWeight: 600, backgroundColor: 'grey.100'}}>
                                    {category}
                                </ListSubheader>,
                                ...settings.map((setting) => (
                                    <MenuItem key={setting.id} value={setting.id} sx={{pl: 3}}>
                                        {setting.title || setting.parameter_name}
                                    </MenuItem>
                                ))
                            ]).flat()}
                        </TextField>
                    </Grid>
                </Grid>
            </CardContent>
            
            <CardActions sx={{justifyContent: 'flex-end', gap: 1}}>
                <Button onClick={handleClose} disabled={state.isLoading}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleSave} 
                    variant="contained" 
                    disabled={state.isLoading}
                >
                    {state.isLoading ? <CircularProgress size={20} /> : 'Save'}
                </Button>
            </CardActions>
        </DialogCard>
    );
});